import React from 'react';
import {
  Dropdown,
  Icon,
  Tag,
  PageItemAvatar,
  ButtonSquare,
} from '../../../shared/ui';
import { IconMap } from '../../../shared/sprite';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Page, UserPermissionToPageRole } from '@distribute/shared/types';

import { useOpenEditor, usePageDropdownActions } from '../hooks';

import { CreatedBy } from '../../../entities/user';

type Props = {
  page: Page;
  onDelete: (id: string) => void;
  onMoveToFolder: (id: string) => void;
  onSharePage: (id: string) => void;
};

export const WorkspacePageCard: React.FC<Props> = ({
  page,
  onDelete,
  onMoveToFolder,
  onSharePage,
}) => {
  const pageItems = usePageDropdownActions({
    page,
    onDelete,
    onMoveToFolder,
    onSharePage,
  });
  const { handlePageEditorClick } = useOpenEditor(page.sequenceNumber);

  const owner = page.userPermissionToPages.find(
    (u) => u.role === UserPermissionToPageRole.OWNER
  );

  return (
    <div
      className="h-[236px] flex flex-col gap-4 p-6 border border-gray-300 cursor-pointer rounded-xl bg-base-white hover:bg-gray-50 hover:shadow-sm"
      role="button"
      onClick={handlePageEditorClick}
    >
      <div className="flex flex-row items-start justify-between">
        <PageItemAvatar page={page} />
        <div onClick={(e) => e.stopPropagation()}>
          <Dropdown
            listStyles="shadow-lg w-45"
            triggerComponent={
              <DropdownMenu.Trigger className="focus:outline-none">
                <ButtonSquare
                  size="sm"
                  variant="tertiary-gray"
                  className="-mr-2"
                >
                  <Icon glyph={IconMap.DotsHorizontal} width={20} />
                </ButtonSquare>
              </DropdownMenu.Trigger>
            }
            items={pageItems}
          />
        </div>
      </div>
      <p className="font-semibold text-gray-900 break-words text-md line-clamp-2">
        {page.content.title || 'Untitled'}
      </p>
      <CreatedBy
        displayName={owner?.user.displayName || ''}
        photoUrl={owner?.user.photoUrl}
        className="mt-auto"
      />
      <div className="flex flex-nowrap items-center justify-between gap-x-2">
        <div className="flex flex-nowrap items-center gap-x-2 min-w-0">
          <Icon glyph={IconMap.EyeBold} className="flex-shrink-0" />
          <p className="min-w-0 text-sm text-gray-700 truncate">
            {page.views} views
          </p>
        </div>
        {page.published && (
          <Tag
            bordered
            color="success"
            className="!py-0.5 !px-2.5 rounded-full font-medium !text-sm block"
          >
            Published
          </Tag>
        )}
      </div>
    </div>
  );
};
